import { useMemo } from 'react'

import { useLightMode } from '@/hooks/useLightMode'

import { Flex } from '@chakra-ui/react'

import { FacilityPill } from '@hivewatch/dark'

import { SOPsListItemFacilitiesData } from '../types/types'

const MAX_COLUMN_CHARS = 60 // Max characters shown in the column (used for width calculation)
const PILL_PADDING = 16 // Total left/right padding for each facility pill (8px left + 8px right)
const PILL_MARGIN = 4 // Space to the right of each pill
const MORE_PILL_CHARS = 8 // Characters needed for "+X more" pill
const PILL_CHAR_WIDTH = 7 // Average width (px) of each character in a pill

const truncateFacilityName = (name: string, maxChars: number): string => {
  if (name.length <= maxChars) return name
  return name.substring(0, maxChars - 3) + '...'
}

export const FacilitiesCell = ({
  facilities,
}: {
  facilities: SOPsListItemFacilitiesData
}) => {
  const mode = useLightMode()

  const facilitiesData = useMemo(() => {
    const activeFacilities = facilities?.filter((f) => !f?.node?.archived) || []
    const names = activeFacilities.map((f) => f?.node?.name).filter(Boolean)

    if (names.length === 0)
      return {
        visibleFacilities: [],
        hiddenFacilities: [],
      }

    let totalWidth = 0
    const visibleFacilities: string[] = []
    const hiddenFacilities: string[] = []

    const reservedWidthForMorePill =
      names.length > 1 ? MORE_PILL_CHARS * PILL_CHAR_WIDTH : 0
    const maxWidthForFacilities =
      MAX_COLUMN_CHARS * PILL_CHAR_WIDTH - reservedWidthForMorePill

    for (const name of names) {
      const truncatedName = truncateFacilityName(name, MAX_COLUMN_CHARS)
      const pillWidth =
        truncatedName.length * PILL_CHAR_WIDTH + PILL_PADDING + PILL_MARGIN

      if (
        totalWidth + pillWidth <= maxWidthForFacilities ||
        visibleFacilities.length === 0
      ) {
        visibleFacilities.push(truncatedName)
        totalWidth += pillWidth
      } else {
        hiddenFacilities.push(name)
      }
    }

    return { visibleFacilities, hiddenFacilities }
  }, [facilities])

  const { visibleFacilities, hiddenFacilities } = facilitiesData

  return (
    <Flex alignItems='center' flexWrap='nowrap' gap='4px' overflow='hidden'>
      {visibleFacilities.map((facilityName, index) => (
        <FacilityPill
          colorScheme={mode}
          data-testid='sopsPage_table_facilityCell'
          key={index}
        >
          {facilityName}
        </FacilityPill>
      ))}
      {hiddenFacilities.length > 0 && (
        <FacilityPill
          colorScheme={mode}
          data-testid='sopsPage_table_moreFacilitiesCell'
        >
          +{hiddenFacilities.length} more
        </FacilityPill>
      )}
    </Flex>
  )
}
